import React from "react";
import { Loader2 } from "lucide-react";
import { ModalShell, formatMoneyXOF, parseTvaPct, parseMoneyToInt } from "./helpers.js";
import CustomerSuggestInput from "../CustomerSuggestInput.jsx";

export default function PrestationFormModal({
  show, onClose,
  editingPrestation,
  formData, setFormData,
  customerSearch, setCustomerSearch,
  pickedCustomer, setPickedCustomer,
  customerName, setCustomerName,
  customerPhone, setCustomerPhone,
  customerEmail, setCustomerEmail,
  customerAddress, setCustomerAddress,
  customers, projects,
  installmentCount, setInstallmentCount,
  installmentFrequency, setInstallmentFrequency,
  installmentStartDate, setInstallmentStartDate,
  installments,
  installmentsError,
  totalTtcRounded,
  saving,
  onSubmit,
  statusConfig,
  paymentModeConfig,
  ensureInstallments,
  updateInstallment,
  setAutoSplit,
}) {
  if (!show) return null;

  function handlePickCustomer(c) {
    setPickedCustomer(c || null);
    setFormData({ ...formData, customer_id: c?.id || "" });
    setCustomerName(c?.name || "");
    setCustomerPhone(c?.phone || "");
    setCustomerEmail(c?.email || "");
    setCustomerSearch(c?.name || "");
  }

  function handleCustomerFieldChange(setter) {
    return (e) => {
      setter(e.target.value);
      if (pickedCustomer) {
        setPickedCustomer(null);
        setFormData((f) => ({ ...f, customer_id: "" }));
      }
    };
  }

  return (
    <ModalShell
      title={editingPrestation ? "Modifier la prestation" : "Créer une prestation"}
      subtitle="Renseigne les infos de la prestation"
      onClose={onClose}
    >
      <form onSubmit={onSubmit} className="space-y-4">

        {/* Client + Projet */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-slate-900">Client *</label>
            <div className="mt-2 space-y-3">
              <CustomerSuggestInput
                valueName={customerSearch}
                valuePhone=""
                onChangeName={(v) => setCustomerSearch(v)}
                onChangePhone={() => {}}
                onPickCustomer={handlePickCustomer}
                pickedCustomer={pickedCustomer}
                placeholder="Rechercher client (nom / téléphone / email)…"
                showPhoneInput={false}
              />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <div className="text-xs font-semibold text-slate-600 uppercase">Nom</div>
                  <input
                    required
                    value={customerName}
                    onChange={handleCustomerFieldChange(setCustomerName)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                    placeholder="Nom client"
                  />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-600 uppercase">Téléphone</div>
                  <input
                    value={customerPhone}
                    onChange={handleCustomerFieldChange(setCustomerPhone)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                    placeholder="Téléphone"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <div className="text-xs font-semibold text-slate-600 uppercase">Email</div>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={handleCustomerFieldChange(setCustomerEmail)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                    placeholder="Email"
                  />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-600 uppercase">Adresse (optionnel)</div>
                  <input
                    value={customerAddress}
                    onChange={handleCustomerFieldChange(setCustomerAddress)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                    placeholder="Adresse"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-900">Projet (optionnel)</label>
            <select
              value={formData.project_id}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
            >
              <option value="">Aucun projet</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Titre */}
        <div>
          <label className="text-sm font-semibold text-slate-900">Titre *</label>
          <input
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
            placeholder="Ex: Consultation fiscale, Audit, Formation…"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-sm font-semibold text-slate-900">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
            rows={3}
            placeholder="Détails, objectifs…"
          />
        </div>

        {/* Montants */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="text-sm font-semibold text-slate-900">Montant HT (XOF) *</label>
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={formData.amount_ht}
              onChange={(e) => setFormData({ ...formData, amount_ht: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-900">TVA %</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={formData.tva_pct}
              onChange={(e) => setFormData({ ...formData, tva_pct: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
              placeholder="Ex: 18"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-900">Montant TTC</label>
            <div className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 font-medium">
              {formatMoneyXOF((parseFloat(formData.amount_ht) || 0) * (1 + parseTvaPct(formData.tva_pct, 18) / 100))}
            </div>
          </div>
        </div>

        {/* Mode paiement */}
        <div>
          <label className="text-sm font-semibold text-slate-900">Mode paiement</label>
          <select
            value={formData.payment_mode}
            onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
          >
            {Object.entries(paymentModeConfig).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>

        {/* Section échéancier */}
        {formData.payment_mode === "INSTALLMENTS" && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase">Nombre de tranches</div>
                <input
                  type="number"
                  min="2"
                  step="1"
                  value={installmentCount}
                  onChange={(e) => { setInstallmentCount(e.target.value); setAutoSplit(true); }}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase">1ère échéance</div>
                <input
                  type="date"
                  value={installmentStartDate}
                  onChange={(e) => { setInstallmentStartDate(e.target.value); setAutoSplit(true); }}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase">Fréquence</div>
                <select
                  value={installmentFrequency}
                  onChange={(e) => { setInstallmentFrequency(e.target.value); setAutoSplit(true); }}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                >
                  <option value="monthly">Mensuel</option>
                  <option value="biweekly">Toutes les 2 semaines</option>
                  <option value="weekly">Hebdo</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-600">
              <span>Total TTC (arrondi): <span className="font-semibold text-slate-900">{totalTtcRounded}</span></span>
              <button
                type="button"
                onClick={() => { setAutoSplit(true); ensureInstallments(); }}
                className="text-slate-700 hover:text-slate-900 underline"
              >
                Répartir automatiquement
              </button>
            </div>

            <div className="space-y-2">
              {installments.map((inst, idx) => (
                <div
                  key={`${idx}-${inst.due_date || "due"}`}
                  className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_160px_200px] items-center"
                >
                  <div className="text-sm font-semibold text-slate-700">Tranche {idx + 1}</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={inst.amount}
                    onChange={(e) => {
                      const parsed = parseMoneyToInt(e.target.value);
                      updateInstallment(idx, { amount: Number.isFinite(parsed) ? parsed : 0 });
                    }}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                    placeholder="Montant"
                  />
                  <input
                    type="date"
                    value={inst.due_date || ""}
                    onChange={(e) => updateInstallment(idx, { due_date: e.target.value })}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  />
                </div>
              ))}
            </div>

            {installmentsError && (
              <div className="text-xs text-rose-600">{installmentsError}</div>
            )}
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-slate-900">Date service</label>
            <input
              type="date"
              value={formData.service_date}
              onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-900">Date limite paiement</label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
            />
          </div>
        </div>

        {/* Statut */}
        <div>
          <label className="text-sm font-semibold text-slate-900">Statut</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
          >
            {Object.entries(statusConfig).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>

        {/* Boutons */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-70"
          >
            {saving ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Enregistrement…
              </span>
            ) : editingPrestation ? "Mettre à jour" : "Créer"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
